import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateField } from '@mui/x-date-pickers/DateField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { memberDeactivateAccount } from '../api';
import { CountrySelectContainer } from '../container/CountrySelectContainer';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import {
  GenderType,
  IAccountInput,
  IAccountResult,
  IIndividualInput,
  IIndividualResult,
} from '../utils/types';
import MembershipConditionsDialog from './MembershipConditionsDialog';
import SmsVerificationDialog from './SmsVerificationDialog';

export type AccountFormProps = {
  onAccountInfoChange: (accountInput: IAccountInput) => void;
  accountInfo?: IAccountResult;
  onSubmit: (password: string, passwordAgain: string) => Promise<void>;
};

export default function AccountForm({
  accountInfo,
  onSubmit,
  onAccountInfoChange,
}: AccountFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [approvedKvkk, setApprovedKvkk] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const disabledAll = Boolean(accountInfo?.id);
  const { token } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(179); // Initial time set to 3 minutes
  const showSnackbar = useAlertSnackbar();

  const onChangeIndividualInfo = (individualInfo: IIndividualInput) => {
    onAccountInfoChange({
      individual: {
        ...accountInfo?.individual,
        ...individualInfo,
      } as IIndividualResult,
    });
  };

  const onChangeFirstName = (firstName: string) => {
    onChangeIndividualInfo({ firstName });
  };

  const onChangeLastName = (lastName: string) => {
    onChangeIndividualInfo({ lastName });
  };

  const onChangeGender = (gender: GenderType) => {
    onChangeIndividualInfo({ gender });
  };

  const onChangeBirthDate = (birthDate: string) => {
    onChangeIndividualInfo({ birthDate });
  };

  const onChangeCitizenshipCountryId = (citizenshipCountryId: number) => {
    onAccountInfoChange({ citizenshipCountryId });
  };

  const onChangeCitizenshipNumber = (citizenshipNumber: string) => {
    onAccountInfoChange({ citizenshipNumber });
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    onSubmit(password, passwordAgain);
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  const openKvkk = () => {
    window.open('https://hisarturizm.com.tr/kvkk-politikamiz/', '_blank');
  };

  const onChangeEmail = (email: string) => {
    onAccountInfoChange({ email });
    checkValidation(email);
  };

  const [formValid, setFormValid] = useState<boolean>(false);
  const [formValidP, setFormValidP] = useState<boolean>(false);
  const [message, setMessage] = useState('');

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const validatePassword = (newPassword: string) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#%+-.?*]).{8,}$/;

    const isValid = regex.test(newPassword);
    setFormValidP(isValid);
  };

  useEffect(() => {
    const isFormValid =
      accountInfo?.individual?.firstName &&
      accountInfo?.individual?.lastName &&
      accountInfo?.individual?.birthDate &&
      accountInfo?.email &&
      approvedKvkk &&
      password &&
      passwordAgain;

    if (accountInfo?.email) {
      checkValidation(accountInfo.email);
    }

    setFormValid(
      !!isFormValid &&
        message === t('accountForm.emailIsValid') &&
        !!formValidP,
    );
  }, [
    accountInfo?.individual?.firstName,
    accountInfo?.individual?.lastName,
    accountInfo?.individual?.birthDate,
    accountInfo?.email,
    approvedKvkk,
    password,
    passwordAgain,
    formValid,
  ]);

  const checkValidation = (email: string) => {
    const rgExp = /^[a-z0-9._]+@[a-z]+\.[a-z]{2,}(\.[a-z]{2,})?(\.[a-z]{2,})?$/;
    if (rgExp.test(email)) {
      setMessage(t('accountForm.emailIsValid'));
    } else if (email === '') {
      setMessage(t('accountForm.pleaseEnterEmail'));
    } else {
      setMessage(t('accountForm.emailIsNotValid'));
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenSmsValidationDialog = async () => {
    setTimeLeft(179);
    setIsDialogOpen(true);
    await fetchInactivateMemberAccount();
  };

  const handleCloseSmsValidationDialog = () => {
    setIsDialogOpen(false);
  };

  const fetchInactivateMemberAccount = async () => {
    try {
      const res = await memberDeactivateAccount(token);

      if (res.status !== 200) {
        const errorData = await res.json();
        const errorMessage = errorData.detail || 'An unknown error occurred.';
        showSnackbar(errorMessage, 'error');
        handleCloseSmsValidationDialog();
      }

      return res;
    } catch (e: any) {
      setError(e.message);
      showSnackbar(e.message, 'error');
      setDialogOpen(false);
    }
  };

  return (
    <>
      <MembershipConditionsDialog
        sx={{ minWidth: '1500px', marginX: '-500px' }}
        open={dialogOpen}
        onClose={handleCloseDialog}
      />
      <form onSubmit={handleSubmit} autoComplete="off">
        <Box>
          <Typography sx={{ mb: 1, display: 'flex' }}>
            {t('accountForm.accountNumber') + ' *'}
          </Typography>
          <TextField
            type="text"
            name="accountNumber" //phone number
            color="secondary"
            size="small"
            value={accountInfo?.phoneNumber || ' '}
            disabled
            required
            sx={{
              width: '100%',
              backgroundColor: '#FAFAFA',
            }}
          />

          {!accountInfo?.id && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                gap: [2, 3],
                flexDirection: ['column', 'row'],
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                  {t('accountForm.countryOfCitizenship') + ' *'}
                </Box>
                <CountrySelectContainer
                  value={accountInfo?.citizenshipCountryId}
                  onChange={onChangeCitizenshipCountryId}
                />
              </Box>

              <Box sx={{ width: '100%' }}>
                <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                  {t('accountForm.citizenshipNumber') + ' *'}
                </Box>
                <TextField
                  type="text"
                  name="citizenshipNumber"
                  color="secondary"
                  size="small"
                  value={accountInfo?.citizenshipNumber || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    e.target.value.match(/^[0-9]*$/) &&
                    onChangeCitizenshipNumber(e.target.value)
                  }
                  required
                  sx={{
                    width: '100%',
                    backgroundColor: '#FAFAFA',
                  }}
                  inputProps={{ maxLength: 11, minLength: 11 }}
                />
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: [2, 3],
              flexDirection: ['column', 'row'],
            }}
          >
            <Box>
              <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                {t('accountForm.name') + ' *'}
              </Box>
              <TextField
                type="text"
                name="name"
                color="secondary"
                size="small"
                value={accountInfo?.individual?.firstName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeFirstName(e.target.value.toLocaleUpperCase('tr-TR'))
                }
                required
                disabled={disabledAll}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
              />
            </Box>

            <Box>
              <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                {t('accountForm.lastName') + ' *'}
              </Box>
              <TextField
                type="text"
                name="lastName"
                color="secondary"
                size="small"
                value={accountInfo?.individual?.lastName || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onChangeLastName(e.target.value.toLocaleUpperCase('tr-TR'))
                }
                required
                disabled={disabledAll}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
              />
            </Box>
          </Box>
          <Box>
            <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
              {t('accountForm.birthday') + ' *'}
            </Box>
            {!isMobile ? (
              <DatePicker
                value={
                  accountInfo?.individual?.birthDate
                    ? dayjs(accountInfo?.individual?.birthDate)
                        .set('hour', 12)
                        .set('minute', 0)
                        .set('second', 0)
                        .toDate()
                    : null
                }
                onChange={(e) => {
                  const adjustedDate = dayjs(e)
                    .set('hour', 12)
                    .set('minute', 0)
                    .set('second', 0)
                    .toDate()
                    .toISOString();
                  onChangeBirthDate(adjustedDate);
                }}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
                slotProps={{ textField: { size: 'small' } }}
                format="dd/MM/yyyy"
                disabled={disabledAll}
              />
            ) : (
              <DateField
                value={
                  accountInfo?.individual?.birthDate
                    ? dayjs(accountInfo?.individual?.birthDate)
                        .set('hour', 12)
                        .set('minute', 0)
                        .set('second', 0)
                        .toDate()
                    : null
                }
                onChange={(e) => {
                  const adjustedDate = dayjs(e)
                    .set('hour', 12)
                    .set('minute', 0)
                    .set('second', 0)
                    .toDate()
                    .toISOString();
                  onChangeBirthDate(adjustedDate);
                }}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
                size="small"
                format="dd/MM/yyyy"
                disabled={disabledAll}
              />
            )}
          </Box>

          <Box style={{ marginBottom: '8px', display: 'flex', marginTop: 24 }}>
            {t('accountForm.gender') + ' *'}
          </Box>

          <FormGroup row sx={{ gap: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={
                    accountInfo?.individual?.gender === 'FEMALE' || false
                  }
                  value="FEMALE"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeGender(e.target.value as GenderType)
                  }
                />
              }
              label={t('accountForm.female')}
              disabled={disabledAll}
            />

            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={accountInfo?.individual?.gender === 'MALE' || false}
                  value="MALE"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeGender(e.target.value as GenderType)
                  }
                />
              }
              label={t('accountForm.male')}
              disabled={disabledAll}
            />
          </FormGroup>

          <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
            {t('accountForm.email') + ' *'}
          </Box>
          <TextField
            type="email"
            name="email"
            color="secondary"
            size="small"
            helperText={message}
            value={accountInfo?.email || ' '}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChangeEmail(e.target.value)
            }
            required
            disabled={disabledAll}
            sx={{
              width: '100%',
              backgroundColor: '#FAFAFA',
            }}
          />

          {!accountInfo?.id && (
            <>
              <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                {t('accountForm.passwd') + ' *'}
              </Box>
              <TextField
                type="password"
                name="passwd"
                color="secondary"
                size="small"
                value={password}
                helperText={t('accountForm.passwordRulesInfoText')}
                onChange={onChangePassword}
                required
                disabled={disabledAll}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                }}
                inputProps={{ minLength: 4 }}
              />
              <Box sx={{ mb: 1, fontSize: '14px', display: 'flex', mt: 2 }}>
                {t('accountForm.passwdAgain') + ' *'}
              </Box>
              <TextField
                type="password"
                name="passwdAgain"
                color="secondary"
                size="small"
                value={passwordAgain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPasswordAgain(e.target.value)
                }
                required
                disabled={disabledAll}
                sx={{
                  width: '100%',
                  backgroundColor: '#FAFAFA',
                  mb: 2,
                }}
                inputProps={{ minLength: 4 }}
              />
            </>
          )}

          {!accountInfo?.id && (
            <>
              <Box sx={{ ml: 2 }} display="flex" alignItems="center">
                <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                  {t('accountForm.acceptTheTermsofRegisterPress')}
                </Typography>
                <Typography
                  sx={{ fontSize: '14px', marginBottom: '8px', marginX: 0.5 }}
                  onClick={handleOpenDialog}
                >
                  <b
                    style={{
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    {' '}
                    {t('accountForm.acceptTheTermsofRegisterMembership')}
                  </b>
                </Typography>
                <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                  {t('accountForm.acceptTheTermsofRegisterAccept')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  color="secondary"
                  checked={approvedKvkk}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setApprovedKvkk(e.target.checked)
                  }
                />
                <Typography sx={{ fontSize: '14px', textAlign: 'left' }}>
                  {t('accountForm.kvkkTextIndividualInformation')}{' '}
                  <b onClick={openKvkk} style={{ cursor: 'pointer' }}>
                    {t('accountForm.kvkkTextExplainText')}
                  </b>{' '}
                  {t('accountForm.kvkkTextRead')}
                </Typography>
              </Box>
            </>
          )}

          {!accountInfo?.id && (
            <Button
              sx={{ width: '100%', mt: 2 }} // Adjust the marginTop as needed
              variant="contained"
              color={approvedKvkk ? 'secondary' : 'inherit'}
              type="submit"
              disabled={!formValid}
            >
              {t('accountForm.submit')}
            </Button>
          )}
        </Box>
      </form>
      {accountInfo?.id && (
        <Button
          onClick={handleOpenSmsValidationDialog}
          variant="contained"
          color="secondary"
          sx={{ mt: 1 }}
        >
          {t('accountDeactivateDialog.deactivateAccountTittle')}
        </Button>
      )}
      <SmsVerificationDialog
        open={isDialogOpen}
        handleClose={handleCloseSmsValidationDialog}
        phoneNumber={accountInfo?.phoneNumber}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
      />
    </>
  );
}
